import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  Chip,
  InputAdornment,
  Pagination,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { TestDefinition, TestPanel, TestType } from '../../types/procedures';
import { ProcedureCodeType, PROCEDURE_CODE_TYPES, PROCEDURE_CODE_TYPE_DESCRIPTIONS } from '../../types/enums';
import { ProcedureService } from '../../services/procedureService';
import { searchProcedureCodesByType } from '../../services/procedureCodesService';
import MacroTextBox from '../utils/MacroTextBox';
import { 
  getFieldLabelsForTestType, 
  createDefaultTestDefinition,
  getTestTypeDisplayName 
} from '../../types/testDefinitionHelpers';

// Create array of test types for UI dropdowns
const TEST_TYPES = Object.values(TestType);

const procedureService = new ProcedureService();

// Styled Components - Modern Theme
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
}));

const StyledTableContainer = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: theme.palette.grey[50],
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  borderRadius: '8px',
  margin: theme.spacing(0.5),
  '&.edit': {
    backgroundColor: theme.palette.primary.light,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  '&.delete': {
    backgroundColor: theme.palette.error.light,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.error.main,
    },
  },
}));

// Dynamic field rendering component
const TestTypeFields: React.FC<{
  testType: TestType;
  formData: any;
  setFormData: (data: any) => void;
  fieldLabels: Record<string, string>;
}> = ({ testType, formData, setFormData, fieldLabels }) => {
  
  const renderLabFields = () => (
    <>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.specimenType || "Specimen Type"}
          value={formData.specimenType || ''}
          onChange={(e) => setFormData({ ...formData, specimenType: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.containerType || "Container Type"}
          value={formData.containerType || ''}
          onChange={(e) => setFormData({ ...formData, containerType: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.units || "Units"}
          value={formData.units || ''}
          onChange={(e) => setFormData({ ...formData, units: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.abnormalFlags || "Abnormal Flags"}
          value={formData.abnormalFlags || ''}
          onChange={(e) => setFormData({ ...formData, abnormalFlags: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.referenceRangeLow || "Reference Range Low"}
          value={formData.referenceRangeLow || ''}
          onChange={(e) => setFormData({ ...formData, referenceRangeLow: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.referenceRangeHigh || "Reference Range High"}
          value={formData.referenceRangeHigh || ''}
          onChange={(e) => setFormData({ ...formData, referenceRangeHigh: e.target.value })}
        />
      </Grid>
    </>
  );

  const renderImagingFields = () => (
    <>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.modality || "Modality"}
          value={formData.modality || ''}
          onChange={(e) => setFormData({ ...formData, modality: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.bodyRegion || "Body Region"}
          value={formData.bodyRegion || ''}
          onChange={(e) => setFormData({ ...formData, bodyRegion: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox 
              checked={formData.contrastRequired || false} 
              onChange={e => setFormData({ ...formData, contrastRequired: e.target.checked })} 
            />
          }
          label={fieldLabels.contrastRequired || "Contrast Required"}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox 
              checked={formData.dicomCompatible || false} 
              onChange={e => setFormData({ ...formData, dicomCompatible: e.target.checked })} 
            />
          }
          label={fieldLabels.dicomCompatible || "DICOM Compatible"}
        />
      </Grid>
    </>
  );

  const renderProcedureFields = () => (
    <>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.performerRole || "Performer Role"}
          value={formData.performerRole || ''}
          onChange={(e) => setFormData({ ...formData, performerRole: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label={fieldLabels.estimatedDuration || "Estimated Duration (minutes)"}
          value={formData.estimatedDuration || 0}
          onChange={(e) => setFormData({ ...formData, estimatedDuration: parseInt(e.target.value) || 0 })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.location || "Location"}
          value={formData.location || ''}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox 
              checked={formData.requiresConsent || false} 
              onChange={e => setFormData({ ...formData, requiresConsent: e.target.checked })} 
            />
          }
          label={fieldLabels.requiresConsent || "Requires Consent"}
        />
      </Grid>
    </>
  );

  const renderSurgicalFields = () => (
    <>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.procedureType || "Procedure Type"}
          value={formData.procedureType || ''}
          onChange={(e) => setFormData({ ...formData, procedureType: e.target.value })}
          placeholder="e.g., Laparoscopic Cholecystectomy"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.specialty || "Specialty"}
          value={formData.specialty || ''}
          onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
          placeholder="e.g., General Surgery"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.surgeonRole || "Surgeon Role"}
          value={formData.surgeonRole || ''}
          onChange={(e) => setFormData({ ...formData, surgeonRole: e.target.value })}
          placeholder="e.g., Attending Surgeon"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.anesthesiaType || "Anesthesia Type"}
          value={formData.anesthesiaType || ''}
          onChange={(e) => setFormData({ ...formData, anesthesiaType: e.target.value })}
          placeholder="e.g., General, Local, Regional"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label={fieldLabels.estimatedDuration || "Estimated Duration (minutes)"}
          value={formData.estimatedDuration || 0}
          onChange={(e) => setFormData({ ...formData, estimatedDuration: parseInt(e.target.value) || 0 })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.location || "Location"}
          value={formData.location || ''}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          placeholder="e.g., OR 1, ASC"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox 
              checked={formData.requiresConsent || false} 
              onChange={e => setFormData({ ...formData, requiresConsent: e.target.checked })} 
            />
          }
          label={fieldLabels.requiresConsent || "Requires Consent"}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox 
              checked={formData.involvesImplants || false} 
              onChange={e => setFormData({ ...formData, involvesImplants: e.target.checked })} 
            />
          }
          label={fieldLabels.involvesImplants || "Involves Implants"}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox 
              checked={formData.requiresBloodBank || false} 
              onChange={e => setFormData({ ...formData, requiresBloodBank: e.target.checked })} 
            />
          }
          label={fieldLabels.requiresBloodBank || "Requires Blood Bank"}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.surgicalPosition || "Surgical Position"}
          value={formData.surgicalPosition || ''}
          onChange={(e) => setFormData({ ...formData, surgicalPosition: e.target.value })}
          placeholder="e.g., Supine, Lithotomy"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label={fieldLabels.preOpInstructions || "Pre-Op Instructions"}
          value={formData.preOpInstructions || ''}
          onChange={(e) => setFormData({ ...formData, preOpInstructions: e.target.value })}
          placeholder="Special pre-operative instructions"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label={fieldLabels.postOpInstructions || "Post-Op Instructions"}
          value={formData.postOpInstructions || ''}
          onChange={(e) => setFormData({ ...formData, postOpInstructions: e.target.value })}
          placeholder="Special post-operative instructions"
        />
      </Grid>
    </>
  );

  const renderPhysicalExamFields = () => (
    <>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.formTemplateId || "Form Template ID"}
          value={formData.formTemplateId || ''}
          onChange={(e) => setFormData({ ...formData, formTemplateId: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.documentationRole || "Documentation Role"}
          value={formData.documentationRole || ''}
          onChange={(e) => setFormData({ ...formData, documentationRole: e.target.value })}
        />
      </Grid>
    </>
  );

  const renderReferralFields = () => (
    <>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.referralType || "Referral Type"}
          value={formData.referralType || ''}
          onChange={(e) => setFormData({ ...formData, referralType: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.specialty || "Specialty"}
          value={formData.specialty || ''}
          onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.referralMethod || "Referral Method"}
          value={formData.referralMethod || ''}
          onChange={(e) => setFormData({ ...formData, referralMethod: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox 
              checked={formData.requiresAuth || false} 
              onChange={e => setFormData({ ...formData, requiresAuth: e.target.checked })} 
            />
          }
          label={fieldLabels.requiresAuth || "Requires Authorization"}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox 
              checked={formData.trackingEnabled || false} 
              onChange={e => setFormData({ ...formData, trackingEnabled: e.target.checked })} 
            />
          }
          label={fieldLabels.trackingEnabled || "Tracking Enabled"}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox 
              checked={formData.attachmentsAllowed || false} 
              onChange={e => setFormData({ ...formData, attachmentsAllowed: e.target.checked })} 
            />
          }
          label={fieldLabels.attachmentsAllowed || "Attachments Allowed"}
        />
      </Grid>
    </>
  );

  const renderRiskAssessmentFields = () => (
    <>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.assessmentTool || "Assessment Tool"}
          value={formData.assessmentTool || ''}
          onChange={(e) => setFormData({ ...formData, assessmentTool: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.scoringMethod || "Scoring Method"}
          value={formData.scoringMethod || ''}
          onChange={(e) => setFormData({ ...formData, scoringMethod: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.formTemplateId || "Form Template ID"}
          value={formData.formTemplateId || ''}
          onChange={(e) => setFormData({ ...formData, formTemplateId: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.abnormalFlags || "Abnormal Flags"}
          value={formData.abnormalFlags || ''}
          onChange={(e) => setFormData({ ...formData, abnormalFlags: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.referenceRangeLow || "Reference Range Low"}
          value={formData.referenceRangeLow || ''}
          onChange={(e) => setFormData({ ...formData, referenceRangeLow: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.referenceRangeHigh || "Reference Range High"}
          value={formData.referenceRangeHigh || ''}
          onChange={(e) => setFormData({ ...formData, referenceRangeHigh: e.target.value })}
        />
      </Grid>
    </>
  );

  const renderInterventionFields = () => (
    <>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.interventionType || "Intervention Type"}
          value={formData.interventionType || ''}
          onChange={(e) => setFormData({ ...formData, interventionType: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.targetCondition || "Target Condition"}
          value={formData.targetCondition || ''}
          onChange={(e) => setFormData({ ...formData, targetCondition: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.deliveryMode || "Delivery Mode"}
          value={formData.deliveryMode || ''}
          onChange={(e) => setFormData({ ...formData, deliveryMode: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.recommendedFrequency || "Recommended Frequency"}
          value={formData.recommendedFrequency || ''}
          onChange={(e) => setFormData({ ...formData, recommendedFrequency: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.billingCode || "Billing Code"}
          value={formData.billingCode || ''}
          onChange={(e) => setFormData({ ...formData, billingCode: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox 
              checked={formData.trackingEnabled || false} 
              onChange={e => setFormData({ ...formData, trackingEnabled: e.target.checked })} 
            />
          }
          label={fieldLabels.trackingEnabled || "Tracking Enabled"}
        />
      </Grid>
    </>
  );

  const renderGenericFields = () => (
    <>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={fieldLabels.responsibleRole || "Responsible Role"}
          value={formData.responsibleRole || ''}
          onChange={(e) => setFormData({ ...formData, responsibleRole: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>{fieldLabels.priority || "Priority"}</InputLabel>
          <Select
            value={formData.priority || 'Routine'}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            label={fieldLabels.priority || "Priority"}
          >
            <MenuItem value="STAT">STAT</MenuItem>
            <MenuItem value="ASAP">ASAP</MenuItem>
            <MenuItem value="Urgent">Urgent</MenuItem>
            <MenuItem value="Routine">Routine</MenuItem>
            <MenuItem value="Emergency">Emergency</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </>
  );

  // Render type-specific fields
  switch (testType) {
    case TestType.LAB:
      return renderLabFields();
    case TestType.IMAGING:
      return renderImagingFields();
    case TestType.PROCEDURE:
      return renderProcedureFields();
    case TestType.SURGICAL:
      return renderSurgicalFields();
    case TestType.PHYSICAL_EXAM:
      return renderPhysicalExamFields();
    case TestType.REFERRAL:
      return renderReferralFields();
    case TestType.RISK_ASSESSMENT:
      return renderRiskAssessmentFields();
    case TestType.INTERVENTION:
      return renderInterventionFields();
    case TestType.GENERIC:
      return renderGenericFields();
    case TestType.MEDICATION:
      return renderGenericFields(); // Use generic fields for medication for now
    default:
      return null;
  }
};

export const TestDefinitionManager: React.FC = () => {
  const [testDefinitions, setTestDefinitions] = useState<TestDefinition[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTest, setEditingTest] = useState<TestDefinition | null>(null);
  const [formData, setFormData] = useState<any>(createDefaultTestDefinition(TestType.GENERIC));
  const [panels, setPanels] = useState<TestPanel[]>([]);
  const [procedureCodes, setProcedureCodes] = useState<Array<{ id: string; procedure_code: string; description?: string }>>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();

  // Debounce search term to improve performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Get field labels for current test type
  const fieldLabels = getFieldLabelsForTestType(formData.testType || 'generic');

  useEffect(() => {
    loadData();
    procedureService.getTestPanels().then(setPanels);
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const definitions = await procedureService.getTestDefinitions();
      setTestDefinitions(definitions);
    } catch (error) {
      console.error('Error loading test definitions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeTypeChange = async (codeType: string) => {
    setFormData({ ...formData, codeType, codeValue: '' });
    
    if (codeType) {
      try {
        // Use optimized search function with built-in timeout and limits
        const codes = await searchProcedureCodesByType(codeType, '', true);
        setProcedureCodes(codes);
        console.log(`✅ Loaded ${codes.length} ${codeType} codes for test definition`);
      } catch (error) {
        console.error('Error fetching procedure codes:', error);
        setProcedureCodes([]);
      }
    } else {
      setProcedureCodes([]);
    }
  };

  const handleTestTypeChange = (newTestType: TestType) => {
    // Create new default form data for the selected test type
    const defaultData = createDefaultTestDefinition(newTestType);
    
    // Keep the user-entered base fields (name, codeType, codeValue)
    setFormData({
      ...defaultData,
      name: formData.name,
      codeType: formData.codeType,
      codeValue: formData.codeValue,
      orderable: formData.orderable,
      reportable: formData.reportable,
      active: formData.active,
    });
  };

  const handleOpenDialog = async (test?: TestDefinition) => {
    if (test) {
      setEditingTest(test);
      
      // Load procedure codes first if code type is set
      let availableCodes: Array<{ id: string; procedure_code: string; description?: string }> = [];
      if (test.codeType) {
        try {
          // Use optimized search function with built-in timeout and limits
          availableCodes = await searchProcedureCodesByType(test.codeType, '', true);
          setProcedureCodes(availableCodes);
          console.log(`✅ Loaded ${availableCodes.length} ${test.codeType} codes for editing test`);
        } catch (error) {
          console.error('Error fetching procedure codes:', error);
          setProcedureCodes([]);
        }
      }
      
      // Validate code value - if it doesn't exist in available codes, clear it
      const codeValueExists = !test.codeValue || 
        !test.codeType || 
        availableCodes.some(code => code.procedure_code === test.codeValue);
      
      // Set form data directly from the test
      setFormData({
        ...test,
        codeValue: codeValueExists ? (test.codeValue || '') : '',
      });
    } else {
      setEditingTest(null);
      setFormData(createDefaultTestDefinition(TestType.GENERIC));
      setProcedureCodes([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTest(null);
    setProcedureCodes([]);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.testType) return;
    const testData = { ...formData };
    if (editingTest) {
      const updatedTest = await procedureService.updateTestDefinition(editingTest.id, testData);
      setTestDefinitions(testDefinitions.map(test => test.id === editingTest.id ? updatedTest : test));
    } else {
      const newTest = await procedureService.createTestDefinition(testData);
      setTestDefinitions([...testDefinitions, newTest]);
    }
    handleCloseDialog();
  };

  const handleDelete = async (testId: string) => {
    // TODO: Implement delete functionality
    // This would require adding a delete method to the ProcedureService
    console.log('Delete test:', testId);
  };

  const filteredTestDefinitions = useMemo(() => {
    return testDefinitions.filter(test => 
      test.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      test.codeValue?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      test.codeType?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
  }, [testDefinitions, debouncedSearchTerm]);

  const totalPages = Math.ceil(filteredTestDefinitions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTestDefinitions = filteredTestDefinitions.slice(startIndex, endIndex);

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <HeaderCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Test Definitions Management
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Define and manage individual test specifications, codes, and parameters
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
              }}
            >
              New Test
            </Button>
          </Box>
        </CardContent>
      </HeaderCard>

      <ModernCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Test Definitions ({filteredTestDefinitions.length} of {testDefinitions.length}
              {debouncedSearchTerm && ` - filtered by "${debouncedSearchTerm}"`})
            </Typography>
            
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Search by name, code, or type..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page when searching
              }}
              sx={{ 
                minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        setCurrentPage(1);
                      }}
                      sx={{ 
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          {isLoading ? (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Loading test definitions...
              </Typography>
            </Box>
          ) : testDefinitions.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No test definitions found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your first test definition to start building test panels
              </Typography>
            </Box>
          ) : filteredTestDefinitions.length === 0 && debouncedSearchTerm ? (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No results found for "{debouncedSearchTerm}"
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search terms or create a new test definition
              </Typography>
            </Box>
          ) : (
            <StyledTableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Name</StyledTableCell>
                    <StyledTableCell>Procedure Code</StyledTableCell>
                    <StyledTableCell>Code Type</StyledTableCell>
                    <StyledTableCell>Category</StyledTableCell>
                    <StyledTableCell>Units</StyledTableCell>
                    <StyledTableCell>Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentTestDefinitions.map((test) => (
                    <TableRow 
                      key={test.id}
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: theme.palette.action.hover 
                        } 
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{test.name}</TableCell>
                      <TableCell>
                        <Box
                          component="code"
                          sx={{
                            backgroundColor: '#f5f5f5',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          {test.codeValue}
                        </Box>
                      </TableCell>
                      <TableCell>{test.codeType}</TableCell>
                      <TableCell>
                        <Chip 
                          label={getTestTypeDisplayName(test.testType)} 
                          size="small"
                          sx={{ 
                            backgroundColor: theme.palette.primary.light,
                            color: theme.palette.primary.contrastText,
                            fontWeight: 500
                          }}
                        />
                      </TableCell>
                      <TableCell>{test.testType === 'Lab' ? (test as any).units : ''}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit">
                            <ActionButton 
                              className="edit"
                              onClick={() => handleOpenDialog(test)}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </ActionButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <ActionButton 
                              className="delete"
                              onClick={() => handleDelete(test.id)}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </ActionButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
          
          {/* Pagination Controls */}
          {filteredTestDefinitions.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredTestDefinitions.length)} of {filteredTestDefinitions.length} results
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Per page</InputLabel>
                  <Select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    label="Per page"
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
                showFirstButton
                showLastButton
                size="large"
              />
            </Box>
          )}
        </CardContent>
      </ModernCard>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
          {editingTest ? 'Edit Test Definition' : 'New Test Definition'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              {/* Top Row - Name and Checkboxes */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly', alignItems: 'center', pt: 1, height: '100%' }}>
                  <FormControlLabel
                    control={<Checkbox checked={formData.orderable || false} onChange={e => setFormData({ ...formData, orderable: e.target.checked })} />}
                    label="Orderable"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={formData.reportable || false} onChange={e => setFormData({ ...formData, reportable: e.target.checked })} />}
                    label="Reportable"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={formData.active !== false} onChange={e => setFormData({ ...formData, active: e.target.checked })} />}
                    label="Active"
                  />
                </Box>
              </Grid>

              {/* Second Row - Test Type and Code Type */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Test Type</InputLabel>
                  <Select
                    value={formData.testType || 'Generic'}
                    onChange={(e) => handleTestTypeChange(e.target.value as TestType)}
                    label="Test Type"
                    required
                  >
                    {TEST_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {getTestTypeDisplayName(type)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Procedure Code Type</InputLabel>
                  <Select
                    value={formData.codeType || ''}
                    onChange={(e) => handleCodeTypeChange(e.target.value)}
                    label="Procedure Code Type"
                  >
                    {PROCEDURE_CODE_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type} - {PROCEDURE_CODE_TYPE_DESCRIPTIONS[type as ProcedureCodeType]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Third Row - Procedure Code */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Procedure Code</InputLabel>
                  <Select
                    value={procedureCodes.some(code => code.procedure_code === formData.codeValue) ? formData.codeValue : ''}
                    onChange={(e) => setFormData({ ...formData, codeValue: e.target.value })}
                    label="Procedure Code"
                    disabled={!formData.codeType}
                  >
                    {procedureCodes.map((code) => (
                      <MenuItem key={code.id} value={code.procedure_code}>
                        {code.procedure_code} - {code.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Notes Field */}
              <Grid item xs={12}>
                <MacroTextBox
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes"
                  value={formData.instructions || ''}
                  onChange={(e: any) => setFormData({ ...formData, instructions: e.target.value })}
                  placeholder="Type /m for macros or @code to expand... (e.g., @lab_orders, @vital_signs)"
                />
              </Grid>

              {/* Dynamic Fields Based on Test Type */}
              <TestTypeFields 
                testType={formData.testType || 'Generic'}
                formData={formData}
                setFormData={setFormData}
                fieldLabels={fieldLabels}
              />

            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || !formData.testType}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            {editingTest ? 'Update' : 'Create'} Test
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 